import {
  ProjectBulkButtonComponents,
  ProjectTableBulkName,
  ProjectTableSubject,
} from "@components/common/faro-table/faro-table-types";
import { BulkChangeProjectAccessLevel } from "@components/table/projects/bulk-actions/bulk-change-project-access-level";
import { BulkChangeProjectManagement } from "@components/table/projects/bulk-actions/bulk-change-project-management";
import { BulkChangeProjectStatus } from "@components/table/projects/bulk-actions/bulk-change-project-status";
import { BulkDeleteProjects } from "@components/table/projects/bulk-actions/bulk-delete-projects";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  BaseProjectsProps,
  ProjectArchivingState,
} from "@custom-types/project-types";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { isProjectDemo } from "@utils/project-utils";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps, BaseProjectsProps {
  /** The archiving status of the project */
  projectStatus: ProjectArchivingState;

  /** Recognizes on which subject the table is being used */
  subjectType: ProjectTableSubject;
}

/** List all the bulk action components for project table */
export function useProjectsPageBulkActions({
  companyId,
  projects,
  projectStatus,
  subjectType,
}: Props): ProjectBulkButtonComponents {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);

  const isAnyRowSelected = selectedEntities.length !== 0;
  const { hasUserPermissionProjectLevel } =
    useHasUserValidPermissionProjectLevel();
  const { hasFeatureProjectLevel } = useHasValidFeatureProjectLevel();

  // Check if the user has the valid role to change the status of any selected project
  const hasUserValidRoleToChangeStatusOfAnyProject = useMemo(() => {
    const requiredRole =
      projectStatus === ProjectArchivingState.archived
        ? RequiredFeatureProjectLevelName.canUnarchiveProject
        : RequiredFeatureProjectLevelName.canArchiveProject;

    return selectedEntities.some((project) =>
      hasFeatureProjectLevel({
        roleName: requiredRole,
        featuresAvailable: project.featuresAvailable ?? [],
      })
    );
  }, [selectedEntities, hasFeatureProjectLevel, projectStatus]);

  const hasUserValidRoleToDeleteAnyProject = selectedEntities.some((project) =>
    hasUserPermissionProjectLevel({
      permissionName: RequiredPermissionProjectLevelName.canDeleteProject,
      project,
    })
  );

  const hasUserValidRoleToChangeAccessOfAnyProject = selectedEntities.some(
    (project) =>
      hasUserPermissionProjectLevel({
        permissionName:
          RequiredPermissionProjectLevelName.canChangeProjectAccessLevel,
        project,
      })
  );

  const hasUserValidPermissionToChangeProjectGroupOfAnyProject =
    selectedEntities.some((project) =>
      isAllowedToChangeManagement(isProjectDemo(project), project)
    );

  const allowedButtonsBasedOnRowSelection: ProjectTableBulkName[] =
    useMemo(() => {
      const allowedButtons: ProjectTableBulkName[] = ["export"];

      if (
        projectStatus === ProjectArchivingState.archived &&
        isAnyRowSelected &&
        hasUserValidRoleToDeleteAnyProject
      ) {
        allowedButtons.push("removeProject");
      }

      if (isAnyRowSelected && hasUserValidRoleToChangeStatusOfAnyProject) {
        allowedButtons.push("changeProjectStatus");
      }

      if (
        isAnyRowSelected &&
        hasUserValidPermissionToChangeProjectGroupOfAnyProject
      ) {
        allowedButtons.push("changeProjectManagement");
      }

      if (
        projectStatus === ProjectArchivingState.active &&
        isAnyRowSelected &&
        hasUserValidRoleToChangeAccessOfAnyProject
      ) {
        allowedButtons.push("changeAccessLevel");
      }

      return allowedButtons;
    }, [
      projectStatus,
      isAnyRowSelected,
      hasUserValidRoleToDeleteAnyProject,
      hasUserValidRoleToChangeStatusOfAnyProject,
      hasUserValidPermissionToChangeProjectGroupOfAnyProject,
      hasUserValidRoleToChangeAccessOfAnyProject,
    ]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Determine whether to show the export button */
  const shouldShowExportButton =
    shouldShowBulkButtons("export") &&
    (isFetchingProjects || projects.length > 0);

  return {
    changeProjectManagement: shouldShowBulkButtons(
      "changeProjectManagement"
    ) ? (
      <BulkChangeProjectManagement companyId={companyId} />
    ) : null,

    changeAccessLevel: shouldShowBulkButtons("changeAccessLevel") ? (
      <BulkChangeProjectAccessLevel />
    ) : null,

    changeProjectStatus: shouldShowBulkButtons("changeProjectStatus") ? (
      <BulkChangeProjectStatus
        projectStatus={projectStatus}
        projects={projects}
      />
    ) : null,

    removeProject: shouldShowBulkButtons("removeProject") ? (
      <BulkDeleteProjects companyId={companyId} />
    ) : null,

    export: shouldShowExportButton ? (
      <BulkExportProjects
        companyId={companyId}
        selectedProjects={isAnyRowSelected ? selectedEntities : projects}
        subjectType={subjectType}
        projectStatus={projectStatus}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,
  };
}
